import React, { useState, useEffect } from 'react';
import firebase from 'firebase/compat/app';
import '../styles/timer.css';
import i18next from 'i18next';

const PRESETS = [
  { label: '30s', seconds: 30 },
  { label: '1min', seconds: 60 },
  { label: '2min', seconds: 120 },
  { label: '5min', seconds: 300 },
];

const TimerWidget = ({ campaignUid, isDm }) => {
  const [timerData, setTimerData] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [customSeconds, setCustomSeconds] = useState('');
  const [label, setLabel] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!campaignUid) return;
    const ref = firebase.database().ref(`timer/${campaignUid}`);
    ref.on('value', snap => setTimerData(snap.val()));
    return () => ref.off();
  }, [campaignUid]);

  useEffect(() => {
    if (!timerData?.active || !timerData?.endAt) {
      setRemaining(0);
      return;
    }
    const tick = () => {
      const diff = Math.max(0, Math.ceil((timerData.endAt - Date.now()) / 1000));
      setRemaining(diff);
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [timerData]);

  const startTimer = (seconds) => {
    firebase.database().ref(`timer/${campaignUid}`).set({
      endAt: Date.now() + seconds * 1000,
      label: label.trim() || null,
      active: true,
    });
  };

  const stopTimer = () => {
    firebase.database().ref(`timer/${campaignUid}`).update({ active: false });
  };

  const resetTimer = () => {
    firebase.database().ref(`timer/${campaignUid}`).remove();
    setLabel('');
  };

  const fmt = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}:${String(sec).padStart(2, '0')}` : `${sec}s`;
  };

  const isActive = timerData?.active && remaining > 0;
  const isExpired = timerData?.active && remaining === 0 && timerData?.endAt;
  const urgent = remaining > 0 && remaining <= 10;

  if (isDm) {
    return (
      <div className="timerDmPanel">
        <button
          className={`timerDmToggle${open ? ' open' : ''}`}
          onClick={() => setOpen(!open)}
        >
          ⏱ {i18next.t('timer.title')}
          {isActive && <span className={`timerDmBadge${urgent ? ' urgent' : ''}`}>{fmt(remaining)}</span>}
          {isExpired && <span className="timerDmBadge expired">0s</span>}
        </button>
        {open && (
          <div className="timerDmControls">
            <input
              className="timerLabelInput"
              type="text"
              placeholder={i18next.t('timer.labelPlaceholder')}
              value={label}
              onChange={e => setLabel(e.target.value)}
              disabled={isActive}
            />
            <div className="timerPresets">
              {PRESETS.map(p => (
                <button
                  key={p.seconds}
                  className="timerPresetBtn"
                  onClick={() => startTimer(p.seconds)}
                  disabled={isActive}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="timerCustomRow">
              <input
                type="number"
                min={5}
                max={3600}
                className="timerCustomInput"
                placeholder={i18next.t('timer.customPlaceholder')}
                value={customSeconds}
                onChange={e => setCustomSeconds(e.target.value)}
                disabled={isActive}
              />
              <button
                className="timerPresetBtn"
                onClick={() => { if (customSeconds) startTimer(parseInt(customSeconds, 10)); }}
                disabled={isActive || !customSeconds}
              >
                {i18next.t('timer.start')}
              </button>
            </div>
            {(isActive || isExpired) && (
              <div className="timerDmActions">
                {isActive && <button className="timerStopBtn" onClick={stopTimer}>{i18next.t('timer.stop')}</button>}
                <button className="timerResetBtn" onClick={resetTimer}>{i18next.t('timer.reset')}</button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  if (!isActive && !isExpired) return null;

  return (
    <div className={`timerPill${urgent ? ' urgent' : ''}${isExpired ? ' expired' : ''}`}>
      {timerData?.label && <span className="timerPillLabel">{timerData.label}</span>}
      <span className="timerPillValue">{isExpired ? i18next.t('timer.expired') : fmt(remaining)}</span>
    </div>
  );
};

export default TimerWidget;
